// ════════════════════════════════════════════════════════════════════════════
//  features/fiscal/transporte/hooks/useMDFe.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { mdfeService } from '../services/mdfeService';
import type { EmitirMDFeRequest, EncerrarMDFeRequest, CancelarMDFeRequest, ListaTransporteFiltros } from '../types';

const KEYS = {
  all:   ['mdfe'] as const,
  lista: (p: number, t: number, filtros: ListaTransporteFiltros) => ['mdfe', 'lista', p, t, filtros] as const,
};

export const useMDFe = (pagina = 1, tamanhoPagina = 20, filtros: ListaTransporteFiltros = {}) => {
  const qc = useQueryClient();

  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: KEYS.lista(pagina, tamanhoPagina, filtros),
    queryFn:  () => mdfeService.listar(pagina, tamanhoPagina, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada letra da busca
  });

  const docs         = paginado?.itens        ?? [];
  const total        = paginado?.total         ?? 0;
  const totalPaginas = paginado?.totalPaginas  ?? 1;
  const contagem     = paginado?.contagem;

  const emitir = useMutation({
    mutationFn: (req: EmitirMDFeRequest) => mdfeService.emitir(req),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      if (res.sucesso) toast.success(`MDF-e emitido — Protocolo: ${res.protocolo ?? '-'}`);
      else toast.warning(`SEFAZ: ${res.cStat} — ${res.xMotivo}`);
    },
    // Recusado depois de gravado: o manifesto aparece na lista, para corrigir e transmitir de novo.
    onError: (e) => { qc.invalidateQueries({ queryKey: KEYS.all }); toast.error(getApiErrorMessage(e)); },
  });

  const transmitir = useMutation({
    mutationFn: (id: number) => mdfeService.transmitir(id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      if (res.sucesso) toast.success(`MDF-e autorizado — Protocolo: ${res.protocolo ?? '-'}`);
      else toast.warning(`SEFAZ: ${res.cStat} — ${res.xMotivo}`);
    },
    // A recusa também muda a linha (o motivo fica gravado no manifesto).
    onError: (e) => { qc.invalidateQueries({ queryKey: KEYS.all }); toast.error(getApiErrorMessage(e)); },
  });

  const encerrar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: EncerrarMDFeRequest }) => mdfeService.encerrar(id, req),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      if (res.sucesso) toast.success('MDF-e encerrado com sucesso');
      else toast.warning(`SEFAZ: ${res.cStat} — ${res.xMotivo}`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const cancelar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: CancelarMDFeRequest }) => mdfeService.cancelar(id, req),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      if (res.sucesso) toast.success('MDF-e cancelado com sucesso');
      else toast.warning(`SEFAZ: ${res.cStat} — ${res.xMotivo}`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return { docs, total, totalPaginas, contagem, isLoading, isFetching, emitir, transmitir, encerrar, cancelar };
};
